import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { UserIcon } from "@phosphor-icons/react/User";
import { ContactsCardBox } from "../CustomComponents/Internal/contactsCardBox";
import { formatPhoneNumberWithExtension } from "@/Shared/Components/formatPhoneNumberWithExtension";
import type { UUID } from "@/Shared/types";
import { useSavedLocationsService } from "@/Shared/Locations/Services/LocationsSavedService";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { SavedContact } from "@freightsimple/generated-dashboard-openapi-client/models/SavedContact";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useEffect, useState } from "react";
import { ManageContactsDialogFlavour } from "../ManageContactsDialogFlavour";
import { ContactsAddContactDialog } from "./ContactsAddContactDialog";
import { ContactsModifyContactDialog } from "./ContactsModifyContactDialog";

function dashIfMissing(s: string | undefined): string {
  if (s === undefined || s === "") {
    return "-";
  } else {
    return s;
  }
}

interface SwitchContactProps {
  contactId: UUID;
  setContactId: (contactId: UUID) => void;
  contacts: Array<SavedContact>;
  defaultSavedContactId: UUID | undefined;
  flavour: ManageContactsDialogFlavour;
}

function SwitchContact_(props: SwitchContactProps) {
  function description(c: SavedContact) {
    const name = c.contact.contactName;

    if (c.savedContactId === props.defaultSavedContactId) {
      if (props.flavour === ManageContactsDialogFlavour.AddressBook) {
        return `${name} (Default)`;
      } else {
        return name;
      }
    } else {
      return name;
    }
  }

  const options = props.contacts.map((c) => {
    return {
      value: c.savedContactId,
      label: description(c),
    };
  });

  // Fills the dialog body, like the card below it.
  return (
    <Dropdown
      value={props.contactId}
      onChange={props.setContactId}
      options={options}
      width="full"
    />
  );
}

type SubDialog =
  | { kind: "add"; openCount: number }
  | { kind: "modify"; contact: SavedContact; openCount: number }
  | { kind: "confirmDelete" };

interface ContactsManageDialogProps extends DialogBaseProps {
  savedLocationId: UUID | undefined;
  flavour: ManageContactsDialogFlavour;
  locationContext?: LocationContext | undefined;
  /** Fired with the selected contact when the user presses Done. */
  onDone: (selectedContact: Contact) => void;
}

// Manage-contacts dialog (address book "Manage Contacts" / after-booking
// "Change Contact"): pick a contact, modify/delete/set-default, add new.
// The add/modify forms and the delete confirm stack on top as their own
// dialogs. Replaces the legacy imperative modal cluster.
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function ContactsManageDialog(props: ContactsManageDialogProps) {
  const { flavour, locationContext } = props;
  const [selectedContactId, setSelectedContactId] = useState<
    UUID | undefined
  >();
  const [savedLocation, setSavedLocation] = useState<
    SavedLocation | undefined
  >();
  const [subDialog, setSubDialog] = useState<SubDialog | undefined>();
  const service = useSavedLocationsService();

  async function loadSavedLocation() {
    if (props.savedLocationId === undefined) {
      return;
    }
    const sl = await service.getLocation(props.savedLocationId);
    setSavedLocation(sl);
    setSelectedContactId(sl.defaultSavedContactId);
  }

  useEffect(
    function () {
      if (props.open) {
        setSavedLocation(undefined);
        loadSavedLocation();
      }
    },
    [props.savedLocationId, props.open],
  );

  if (
    flavour === ManageContactsDialogFlavour.ModifyAfterBooking &&
    locationContext === undefined
  ) {
    throw new Error("Missing locationContext");
  }

  const selectedContact = savedLocation?.contacts.find(
    (sc) => sc.savedContactId === selectedContactId,
  );

  const loaded =
    savedLocation !== undefined &&
    selectedContactId !== undefined &&
    selectedContact !== undefined;

  async function onAdded(newContact: SavedContact) {
    setSubDialog(undefined);
    await service.addContact(newContact);
    await loadSavedLocation();
    setSelectedContactId(newContact.savedContactId);
  }

  async function onModified(modifiedContact: SavedContact) {
    setSubDialog(undefined);
    await service.modifyContact(
      modifiedContact.savedContactId,
      modifiedContact.contact,
    );
    await loadSavedLocation();
    setSelectedContactId(modifiedContact.savedContactId);
  }

  async function onDeleteConfirmed() {
    if (selectedContactId === undefined) {
      throw new Error("Missing selectedContactId");
    }
    setSubDialog(undefined);
    await service.deleteContact(selectedContactId);
    await loadSavedLocation();
  }

  async function onSetContactAsDefault() {
    if (
      props.savedLocationId === undefined ||
      selectedContactId === undefined
    ) {
      throw new Error("No selectedContactId");
    }

    await service.setDefaultContact(props.savedLocationId, selectedContactId);
    await loadSavedLocation();
  }

  function onDone() {
    if (selectedContact === undefined) {
      throw new Error("onDone");
    }
    props.onDone(selectedContact.contact);
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title={
          flavour === ManageContactsDialogFlavour.AddressBook
            ? "Manage Contacts"
            : "Change Contact"
        }
        description={
          flavour === ManageContactsDialogFlavour.AddressBook
            ? "Select a contact to manage"
            : "You can edit details of the current contact, pick a different one, or add a new one. Changes will also be saved to your address book."
        }
        width="narrow"
        footer={
          <>
            {flavour === ManageContactsDialogFlavour.ModifyAfterBooking && (
              <Button onClick={() => props.onOpenChange?.(false)}>
                Cancel
              </Button>
            )}
            <Button
              primary
              onClick={onDone}
              disabled={!loaded}
              disabledMessage="Loading contacts..."
            >
              Done
            </Button>
          </>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {!loaded && <Spinner size={32} />}
          {loaded && (
            <Stack gap={6}>
              <Stack gap={4}>
                {savedLocation.contacts.length > 1 && (
                  <SwitchContact_
                    contactId={selectedContactId}
                    setContactId={setSelectedContactId}
                    contacts={savedLocation.contacts}
                    defaultSavedContactId={savedLocation.defaultSavedContactId}
                    flavour={flavour}
                  />
                )}

                <ContactsCardBox>
                  <Row justify="between" align="start">
                    <Stack gap={4} align="start">
                      <Row gap={2}>
                        <Icon
                          icon={UserIcon}
                          size="lg"
                          weight="fill"
                          color="text-brand-text"
                        />
                        <T size="lg" weight="medium" color="foreground">
                          {selectedContact.contact.contactName}
                        </T>
                      </Row>
                      <Stack gap={0} align="start">
                        <T size="xs" weight="light" color="muted">
                          Phone Number
                        </T>
                        <T>
                          {formatPhoneNumberWithExtension(
                            selectedContact.contact.phoneNumber,
                            selectedContact.contact.phoneNumberExtension,
                          )}
                        </T>
                      </Stack>
                      <Stack gap={0} align="start">
                        <T size="xs" weight="light" color="muted">
                          Email Address
                        </T>
                        <T>
                          {dashIfMissing(selectedContact.contact.emailAddress)}
                        </T>
                      </Stack>
                    </Stack>
                    <Stack gap={1} align="end">
                      <Button
                        size="link"
                        onClick={() =>
                          setSubDialog((previous) => ({
                            kind: "modify",
                            contact: selectedContact,
                            openCount:
                              (previous && "openCount" in previous
                                ? previous.openCount
                                : 0) + 1,
                          }))
                        }
                      >
                        Modify
                      </Button>
                      {/* Can only delete if there is more than one left */}
                      {savedLocation.contacts.length > 1 &&
                        flavour === ManageContactsDialogFlavour.AddressBook && (
                          <Button
                            size="link"
                            onClick={() =>
                              setSubDialog({ kind: "confirmDelete" })
                            }
                          >
                            Delete
                          </Button>
                        )}
                      {flavour === ManageContactsDialogFlavour.AddressBook && (
                        <Button size="link" onClick={onSetContactAsDefault}>
                          Set as default
                        </Button>
                      )}
                    </Stack>
                  </Row>
                </ContactsCardBox>
              </Stack>
              <Row>
                <Button
                  size="link"
                  onClick={() =>
                    setSubDialog((previous) => ({
                      kind: "add",
                      openCount:
                        (previous && "openCount" in previous
                          ? previous.openCount
                          : 0) + 1,
                    }))
                  }
                >
                  Add new contact
                </Button>
              </Row>
            </Stack>
          )}
        </ControlPresentationProvider>
      </Dialog>

      {props.savedLocationId !== undefined && (
        <ContactsAddContactDialog
          key={subDialog?.kind === "add" ? subDialog.openCount : "add-closed"}
          open={subDialog?.kind === "add"}
          onOpenChange={(open) => {
            if (!open) setSubDialog(undefined);
          }}
          savedLocationId={props.savedLocationId}
          onAddNewContact={onAdded}
        />
      )}
      {subDialog?.kind === "modify" && (
        <ContactsModifyContactDialog
          key={subDialog.openCount}
          open
          onOpenChange={(open) => {
            if (!open) setSubDialog(undefined);
          }}
          contactToModify={subDialog.contact}
          onModified={onModified}
        />
      )}
      <Dialog
        open={subDialog?.kind === "confirmDelete"}
        onOpenChange={(open) => {
          if (!open) setSubDialog(undefined);
        }}
        title="Confirm delete"
        description="Are you sure you want to delete this contact?"
        okText="Delete Contact"
        onOk={onDeleteConfirmed}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
    </>
  );
}
